import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
  ProgressBar,
  Form,
  InputGroup,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaHardHat,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaFileContract,
  FaCalendarAlt,
  FaCalendarCheck,
  FaCalendarDay,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaBuilding,
  FaTools,
  FaChartLine,
  FaInfoCircle,
  FaSearch,
  FaFilter,
  FaSortAmountDown,
  FaSortAmountDownAlt,
  FaClipboardCheck,
  FaProjectDiagram,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const SubContractorsBidList = () => {
  const { projectId } = useParams(); // Changed from taskId to projectId
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subcontractors, setSubcontractors] = useState([]);
  const [projectInfo, setProjectInfo] = useState(null); // Added projectInfo
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch subcontractors for the project
      const res = await axios.get(
        `${API_BASE}/data/getSubcontractors/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.status === 'success') {
        setSubcontractors(res.data.data || []);
      } else {
        toast.error('Failed to load subcontractors');
      }
      
      // Try to fetch project info if possible
      await fetchProjectInfo();
      
    } catch (error) {
      console.error('Fetch data error:', error);
      toast.error('Failed to load subcontractors');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectInfo = async () => {
    try {
      // Since getProjectById doesn't exist, we'll try to get projects by user
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        const res = await axios.get(
          `${API_BASE}/data/getProjectsByUser/${user._id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (res.data.success && res.data.projects) {
          // Find the project by ID
          const project = res.data.projects.find(p => p._id === projectId);
          if (project) {
            setProjectInfo(project);
          }
        }
      }
    } catch (error) {
      console.error('Fetch project info error:', error);
      // Silently fail - project info is not essential
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId]);

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Not provided';
    // Simple phone formatting
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  /* ================= STATUS & FILTERS ================= */
  const getStatusBadge = (scheduledDate, installedDate) => {
    const today = new Date();
    const scheduled = new Date(scheduledDate);
    const installed = installedDate ? new Date(installedDate) : null;

    if (installed) {
      return <Badge bg="success" className="px-2 py-1">Completed</Badge>;
    } else if (scheduled < today) {
      return <Badge bg="danger" className="px-2 py-1">Overdue</Badge>;
    } else if (scheduled.toDateString() === today.toDateString()) {
      return <Badge bg="warning" text="dark" className="px-2 py-1">Today</Badge>;
    } else if (scheduled < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
      return <Badge bg="info" className="px-2 py-1">Upcoming</Badge>;
    } else {
      return <Badge bg="secondary" className="px-2 py-1">Scheduled</Badge>;
    }
  };

  const getInsuranceBadge = (value) => {
    if (value === true || value === 'true') {
      return <Badge bg="success" className="px-2 py-1">Yes</Badge>;
    } else if (value === false || value === 'false') {
      return <Badge bg="danger" className="px-2 py-1">No</Badge>;
    } else {
      return <Badge bg="secondary" className="px-2 py-1">Not Set</Badge>;
    }
  };

  /* ================= FILTER & SORT ================= */
  const filteredSubcontractors = subcontractors
    .filter(sub => {
      // Filter by status
      if (filterStatus !== 'all') {
        const today = new Date();
        const scheduled = new Date(sub.scheduledDate);
        const installed = sub.installedDate ? new Date(sub.installedDate) : null;
        
        let status = '';
        if (installed) {
          status = 'completed';
        } else if (scheduled < today) {
          status = 'overdue';
        } else if (scheduled.toDateString() === today.toDateString()) {
          status = 'today';
        } else if (scheduled < new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)) {
          status = 'upcoming';
        } else {
          status = 'scheduled';
        }
        
        if (filterStatus !== status) return false;
      }
      
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          sub.subcontractorName?.toLowerCase().includes(searchLower) ||
          sub.taskName?.toLowerCase().includes(searchLower) ||
          sub.email?.toLowerCase().includes(searchLower) ||
          sub.phone?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
          aVal = a.subcontractorName || '';
          bVal = b.subcontractorName || '';
          break;
        case 'task':
          aVal = a.taskName || '';
          bVal = b.taskName || '';
          break;
        case 'scheduled':
          aVal = new Date(a.scheduledDate).getTime();
          bVal = new Date(b.scheduledDate).getTime();
          break;
        case 'installed':
          aVal = a.installedDate ? new Date(a.installedDate).getTime() : 0;
          bVal = b.installedDate ? new Date(b.installedDate).getTime() : 0;
          break;
        default:
          aVal = a.subcontractorName || '';
          bVal = b.subcontractorName || '';
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  /* ================= CALCULATIONS ================= */
  const calculateStats = () => {
    const total = subcontractors.length;
    const completed = subcontractors.filter(s => s.installedDate).length;
    const overdue = subcontractors.filter(s => 
      !s.installedDate && new Date(s.scheduledDate) < new Date()
    ).length;
    const upcoming = subcontractors.filter(s => 
      !s.installedDate && new Date(s.scheduledDate) >= new Date()
    ).length;
    
    const withInsurance = subcontractors.filter(s => 
      s.workersCompensation === true || s.liabilityInsurance === true
    ).length;
    
    const withAgreement = subcontractors.filter(s => s.agreementSigned === true).length;

    return { total, completed, overdue, upcoming, withInsurance, withAgreement };
  };

  const calculateProgress = () => {
    const stats = calculateStats();
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  };

  const handleRefresh = async () => {
    await fetchData();
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading subcontractors...</span>
      </div>
    );
  }

  const stats = calculateStats();
  const progress = calculateProgress();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Projects List
              </Button>
              <h2 className="fw-bold mb-2">
                <FaHardHat className="me-3" />
                Subcontractors Management
              </h2>
              
              <div className="mb-3">
                <h4 className="mb-1">
                  <FaProjectDiagram className="me-2 text-primary" />
                  {projectInfo ? projectInfo.name : 'Project Subcontractors'}
                </h4>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <Badge bg="info" className="px-3 py-2">
                    <FaHardHat className="me-2" />
                    {subcontractors.length} Subcontractor{subcontractors.length !== 1 ? 's' : ''}
                  </Badge>
                  {projectInfo && (
                    <Badge bg="secondary" className="px-3 py-2">
                      <FaBuilding className="me-2" />
                      {projectInfo.name} • Lot: {projectInfo.lotNo}
                    </Badge>
                  )}
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <FaClipboardCheck className="me-2" />
                    Project ID: {projectId?.substring(0, 12)}...
                  </Badge>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-4">
                  <span className="text-muted me-2">Completion:</span>
                  <Badge bg={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}>
                    {progress}%
                  </Badge>
                </div>
                <div>
                  <span className="text-muted me-2">Subcontractors:</span>
                  <Badge bg="dark">{subcontractors.length}</Badge>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={handleRefresh}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* PROGRESS BAR */}
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Overall Completion</small>
              <small className="text-muted">{progress}%</small>
            </div>
            <ProgressBar 
              now={progress} 
              variant={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}
              style={{ height: '8px' }}
            />
          </div>

          {/* FILTERS */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              <Row className="g-3">
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search subcontractors..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaFilter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="overdue">Overdue</option>
                      <option value="today">Today</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="scheduled">Scheduled</option>
                    </Form.Select>
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>Sort by</InputGroup.Text>
                    <Form.Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="name">Subcontractor Name</option>
                      <option value="task">Task Name</option>
                      <option value="scheduled">Scheduled Date</option>
                      <option value="installed">Installed Date</option>
                    </Form.Select>
                    <Button
                      variant={sortOrder === 'asc' ? 'primary' : 'outline-primary'}
                      onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      {sortOrder === 'asc' ? <FaSortAmountDown /> : <FaSortAmountDownAlt />}
                    </Button>
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* STATISTICS */}
      {subcontractors.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-light">
            <h4 className="mb-0">
              <FaChartLine className="me-2" />
              Subcontractors Overview
            </h4>
          </Card.Header>
          <Card.Body>
            <Row className="text-center">
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-primary bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2">{stats.total}</div>
                    <div className="text-muted">Total</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-success bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-success">{stats.completed}</div>
                    <div className="text-muted">Completed</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-warning bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-warning">{stats.upcoming}</div>
                    <div className="text-muted">Upcoming</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-danger bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-danger">{stats.overdue}</div>
                    <div className="text-muted">Overdue</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-info bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-info">{stats.withInsurance}</div>
                    <div className="text-muted">With Insurance</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-dark bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2">{stats.withAgreement}</div>
                    <div className="text-muted">Agreement Signed</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* SUBCONTRACTORS TABLE */}
      {filteredSubcontractors.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaHardHat size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Subcontractors Found</h4>
            <p className="text-muted mb-4">
              {searchTerm || filterStatus !== 'all'
                ? 'No subcontractors match your search criteria'
                : 'No subcontractors have been assigned to this project yet.'}
            </p>
            {(searchTerm || filterStatus !== 'all') && (
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Subcontractor</th>
                    <th>Task Name</th>
                    <th>Contact Info</th>
                    <th>Schedule Dates</th>
                    <th>Documents & Insurance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubcontractors.map((sub, index) => (
                    <tr key={sub.schTaskID || index}>
                      <td>
                        <Badge bg="light" text="dark" className="px-2 py-1">
                          {index + 1}
                        </Badge>
                      </td>
                      <td>
                        <div className="fw-bold">{sub.subcontractorName}</div>
                        <small className="text-muted">
                          {sub.schTaskID ? `Task ID: ${sub.schTaskID.substring(0, 8)}...` : 'No Task ID'}
                        </small>
                      </td>
                      <td>
                        <div className="fw-medium">{sub.taskName}</div>
                      </td>
                      <td>
                        <div className="small">
                          <div className="mb-1">
                            <FaEnvelope className="me-1 text-muted" size={12} />
                            {sub.email || 'Not provided'}
                          </div>
                          <div>
                            <FaPhone className="me-1 text-muted" size={12} />
                            {formatPhone(sub.phone)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          <div className="mb-1">
                            <FaCalendarDay className="me-1 text-primary" size={12} />
                            <span className="fw-medium">{formatDate(sub.scheduledDate)}</span>
                            <div className="text-muted">Scheduled</div>
                          </div>
                          <div>
                            <FaCalendarCheck className={`me-1 ${sub.installedDate ? 'text-success' : 'text-muted'}`} size={12} />
                            <span className={sub.installedDate ? 'fw-medium text-success' : 'text-muted'}>
                              {sub.installedDate ? formatDate(sub.installedDate) : 'Not installed'}
                            </span>
                            <div className="text-muted">Installed</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="small">
                          <div className="d-flex align-items-center mb-1">
                            <FaFileContract className="me-1 text-muted" size={12} />
                            <span className="me-2">Workers Comp:</span>
                            {getInsuranceBadge(sub.workersCompensation)}
                          </div>
                          <div className="d-flex align-items-center mb-1">
                            <FaFileContract className="me-1 text-muted" size={12} />
                            <span className="me-2">Liability Ins:</span>
                            {getInsuranceBadge(sub.liabilityInsurance)}
                          </div>
                          <div className="d-flex align-items-center">
                            <FaFileContract className="me-1 text-muted" size={12} />
                            <span className="me-2">Agreement:</span>
                            {getInsuranceBadge(sub.agreementSigned)}
                          </div>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(sub.scheduledDate, sub.installedDate)}
                        <div className="mt-2 small text-muted">
                          {sub.installedDate ? 'Installed' : 'Not installed yet'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            
            {/* SUMMARY */}
            <div className="p-3 bg-light border-top">
              <Row>
                <Col md={4}>
                  <div className="small text-muted">
                    Showing {filteredSubcontractors.length} of {subcontractors.length} subcontractors
                    {searchTerm && ` matching "${searchTerm}"`}
                  </div>
                </Col>
                <Col md={8} className="text-end">
                  <div className="d-flex justify-content-end gap-4">
                    <div className="text-center">
                      <div className="fw-bold text-primary">{stats.total}</div>
                      <small className="text-muted">Total</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold text-success">{stats.completed}</div>
                      <small className="text-muted">Completed</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold text-warning">{stats.upcoming}</div>
                      <small className="text-muted">Upcoming</small>
                    </div>
                    <div className="text-center">
                      <div className="fw-bold text-danger">{stats.overdue}</div>
                      <small className="text-muted">Overdue</small>
                    </div>
                    <div className="text-center">
                      <div className={`fw-bold ${progress === 100 ? 'text-success' : progress >= 50 ? 'text-info' : 'text-warning'}`}>
                        {progress}%
                      </div>
                      <small className="text-muted">Completion</small>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* EMPTY STATE INFO */}
      {subcontractors.length === 0 && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body>
            <Alert variant="info" className="mb-0">
              <div className="d-flex align-items-center">
                <FaInfoCircle className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading mb-2">Subcontractors Management</h5>
                  <p className="mb-0">
                    This page displays all subcontractors for this project.
                    Subcontractors are external contractors hired for specific tasks
                    with their schedules, contact information, and insurance documentation.
                  </p>
                </div>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default SubContractorsBidList;